import { isMfaChallenge, type LoginResult, type SessionView, type SignupAvailability } from "@dentos/contracts";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { Button, ErrorState, Field, FormError, Input, Loading } from "../components/ui";
import { ApiError, api, qs } from "../lib/api";
import { useSession } from "../lib/session";
import { useBranding } from "../lib/branding";

/** Only allow same-site relative redirects after login (no open redirect). */
const safeNext = (next: string | null) => (next && next.startsWith("/") && !next.startsWith("//") ? next : "/");

function afterLogin(session: SessionView, next: string) {
  if (session.activeTenant) return next;
  if (session.tenants.length > 0) return "/choose-clinic";
  return session.isPlatformAdmin ? "/platform" : "/choose-clinic";
}

function Title({ children, sub }: { children: string; sub?: string }) {
  const { productName } = useBranding();
  useEffect(() => {
    document.title = `${children} · ${productName}`;
  }, [children, productName]);
  return (
    <div className="mb-5">
      <h1 className="text-lg font-semibold">{children}</h1>
      {sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}
    </div>
  );
}

export function LoginPage() {
  const { session, setSession } = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [needsCode, setNeedsCode] = useState(false);
  const finish = (s: SessionView) => {
    setSession(s);
    navigate(afterLogin(s, safeNext(params.get("next"))), { replace: true });
  };
  const login = useMutation({
    mutationFn: () => api.post<LoginResult>("/api/auth/login", { email, password }),
    onSuccess: (r) => {
      if (isMfaChallenge(r)) {
        setNeedsCode(true);
        setPassword("");
      } else finish(r);
    },
  });
  const verify = useMutation({
    mutationFn: () => api.post<SessionView>("/api/auth/mfa/verify", { code }),
    onSuccess: finish,
    onError: (e) => {
      // Expired or too many wrong codes: start again from the password.
      if (e instanceof ApiError && e.status === 401) {
        setNeedsCode(false);
        setCode("");
      }
    },
  });
  if (session) return <Navigate to={afterLogin(session, "/")} replace />;
  if (needsCode) {
    return (
      <form
        className="space-y-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          verify.mutate();
        }}
      >
        <Title sub="Open your authenticator app and enter the 6-digit code it shows.">Two-step sign-in</Title>
        <FormError error={verify.error} />
        <Field label="Code" hint="Lost your phone? Enter one of your recovery codes instead.">
          {(id) => <Input id={id} autoComplete="one-time-code" inputMode="text" className="font-mono" value={code} onChange={(e) => setCode(e.target.value)} autoFocus />}
        </Field>
        <Button type="submit" busy={verify.isPending} className="w-full" disabled={code.trim().length < 6}>
          Verify
        </Button>
        <button type="button" className="block w-full text-center text-sm text-brand-700 hover:underline" onClick={() => setNeedsCode(false)}>
          Back to sign in
        </button>
      </form>
    );
  }
  const submit = (e: FormEvent) => {
    e.preventDefault();
    login.mutate();
  };
  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <Title>Sign in</Title>
      <FormError error={login.error} />
      <Field label="Email">
        {(id) => <Input id={id} type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}
      </Field>
      <Field label="Password">
        {(id) => <Input id={id} type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)} />}
      </Field>
      <Button type="submit" busy={login.isPending} className="w-full" disabled={!email || !password}>
        Sign in
      </Button>
      <p className="text-center text-sm">
        <Link to="/forgot-password" className="text-brand-700 hover:underline">
          Forgot password?
        </Link>
      </p>
      <SignupLink />
    </form>
  );
}

/** "New clinic?" link, only while self-serve sign-up is open. */
function SignupLink() {
  const st = useQuery({ queryKey: ["signup-status"], queryFn: () => api.get<SignupAvailability>("/api/public/signup"), staleTime: 300_000 });
  if (!st.data?.open) return null;
  return (
    <p className="text-center text-sm text-slate-600">
      New clinic?{" "}
      <Link to="/signup" className="text-brand-700 hover:underline">
        Start a free trial
      </Link>
    </p>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const m = useMutation({ mutationFn: () => api.post("/api/auth/forgot-password", { email }) });
  if (m.isSuccess) {
    return (
      <div className="space-y-4">
        <Title sub="If an account exists for that email, a reset link is on its way. It works for 60 minutes.">Check your email</Title>
        <p className="text-sm text-slate-500">No email? Ask your clinic administrator to help you sign in.</p>
        <Link to="/login" className="text-sm text-brand-700 hover:underline">
          Back to sign in
        </Link>
      </div>
    );
  }
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <Title sub="We'll email you a link to set a new password.">Reset password</Title>
      <FormError error={m.error} />
      <Field label="Email">{(id) => <Input id={id} type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />}</Field>
      <Button type="submit" busy={m.isPending} className="w-full" disabled={!email}>
        Send reset link
      </Button>
      <Link to="/login" className="block text-center text-sm text-brand-700 hover:underline">
        Back to sign in
      </Link>
    </form>
  );
}

function NewPasswordFields({ password, setPassword, confirm, setConfirm, error }: { password: string; setPassword: (v: string) => void; confirm: string; setConfirm: (v: string) => void; error?: string }) {
  const mismatch = confirm.length > 0 && confirm !== password;
  return (
    <>
      <Field label="New password" hint="At least 10 characters. A short sentence works well." error={error}>
        {(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
      </Field>
      <Field label="Repeat password" error={mismatch ? "Passwords do not match" : undefined}>
        {(id, d) => <Input id={id} aria-describedby={d} aria-invalid={mismatch} type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />}
      </Field>
    </>
  );
}

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const m = useMutation({ mutationFn: () => api.post("/api/auth/reset-password", { token, password }) });
  if (m.isSuccess) {
    return (
      <div className="space-y-4">
        <Title sub="You can now sign in with your new password.">Password changed</Title>
        <Link to="/login" className="text-sm text-brand-700 hover:underline">
          Sign in
        </Link>
      </div>
    );
  }
  const fieldError = m.error instanceof ApiError && m.error.code === "validation_failed" ? m.error.message : undefined;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        m.mutate();
      }}
    >
      <Title>Choose a new password</Title>
      {!fieldError && <FormError error={m.error} />}
      <NewPasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} error={fieldError} />
      <Button type="submit" busy={m.isPending} className="w-full" disabled={password.length < 10 || password !== confirm}>
        Save password
      </Button>
    </form>
  );
}

export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const navigate = useNavigate();
  const { setSession } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [fullName, setFullName] = useState<string | null>(null);
  const invite = useQuery({
    queryKey: ["invitation", token],
    queryFn: () => api.get<{ email: string; fullName: string; clinicName: string; needsPassword: boolean }>(`/api/auth/invitation${qs({ token })}`),
    retry: false,
  });
  const accept = useMutation({
    mutationFn: () => api.post<SessionView>("/api/auth/accept-invitation", { token, password, fullName: fullName ?? undefined }),
    onSuccess: (s) => {
      setSession(s);
      navigate("/", { replace: true });
    },
  });
  if (invite.isPending) return <Loading />;
  if (invite.isError) return <ErrorState error={invite.error} />;
  const i = invite.data;
  const fieldError = accept.error instanceof ApiError && accept.error.code === "validation_failed" ? accept.error.message : undefined;
  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        accept.mutate();
      }}
    >
      <Title sub={`You're joining ${i.clinicName} as ${i.email}.`}>Welcome</Title>
      {!fieldError && <FormError error={accept.error} />}
      {i.needsPassword ? (
        <>
          <Field label="Your name">{(id) => <Input id={id} autoComplete="name" value={fullName ?? i.fullName} onChange={(e) => setFullName(e.target.value)} />}</Field>
          <NewPasswordFields password={password} setPassword={setPassword} confirm={confirm} setConfirm={setConfirm} error={fieldError} />
        </>
      ) : (
        <Field label="Your existing password" hint="You already have an account. Sign in to add this clinic.">
          {(id, d) => <Input id={id} aria-describedby={d} type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} />}
        </Field>
      )}
      <Button type="submit" busy={accept.isPending} className="w-full" disabled={!password || (i.needsPassword && password !== confirm)}>
        Join {i.clinicName}
      </Button>
    </form>
  );
}

export function ChooseClinicPage() {
  const { session, setSession, loading } = useSession();
  const navigate = useNavigate();
  const choose = useMutation({
    mutationFn: (tenantId: string) => api.post<SessionView>("/api/auth/switch-tenant", { tenantId }),
    onSuccess: (s) => {
      setSession(s);
      navigate("/", { replace: true });
    },
  });
  if (loading) return <Loading />;
  if (!session) return <Navigate to="/login" replace />;
  return (
    <div className="space-y-4">
      <Title sub="You have access to more than one clinic.">Choose a clinic</Title>
      <FormError error={choose.error} />
      {session.tenants.length === 0 ? (
        <p className="text-sm text-slate-600">Your account is not linked to any clinic yet. Ask the clinic that invited you to send a new invitation.</p>
      ) : (
        <ul className="space-y-2">
          {session.tenants.map((t) => (
            <li key={t.id}>
              <Button variant="secondary" className="w-full justify-between" onClick={() => choose.mutate(t.id)} busy={choose.isPending && choose.variables === t.id}>
                {t.name}
                {t.status === "suspended" && <span className="text-xs text-red-600">Suspended</span>}
              </Button>
            </li>
          ))}
        </ul>
      )}
      {session.isPlatformAdmin && (
        <Link to="/platform" className="block text-center text-sm text-brand-700 hover:underline">
          Platform admin
        </Link>
      )}
    </div>
  );
}
